import { useTranslation } from "react-i18next";
import { TbAlertTriangle } from "react-icons/tb";
import { displayCode, requiredCodesLabel } from "src/helpers/goaRules";
import type { GoaLineIssues } from "./useGoaLineIssues";
import "./goaLineEditor.scss";

/** Banners above a GoaLineEditor: duplicate codes, "not billable together" pairs, frequency caps. */
export function GoaLineIssueBanners({ issues }: { issues: GoaLineIssues }) {
    const { t } = useTranslation();
    const { duplicateCodes, conflicts, maxCountViolations } = issues;
    const overLimit = maxCountViolations.filter((v) => v.kind === "overLimit");
    const missingBase = maxCountViolations.filter((v) => v.kind === "missingRequiredCode");

    return (
        <>
            {duplicateCodes.size > 0 && (
                <div className="goa-line-editor__conflict-banner goa-line-editor__conflict-banner--warn">
                    <TbAlertTriangle />
                    <div>
                        <strong>
                            {t("invoicePage.duplicateCodeBanner", {
                                codes: [...duplicateCodes].map(displayCode).join(", "),
                            })}
                        </strong>
                        <p className="goa-line-editor__banner-hint">{t("invoicePage.duplicateCodeHint")}</p>
                    </div>
                </div>
            )}
            {conflicts.length > 0 && (
                <div className="goa-line-editor__conflict-banner">
                    <TbAlertTriangle />
                    <div>
                        <strong>{t("invoicePage.goaConflictBadge", { count: conflicts.length })}</strong>
                        <ul>
                            {conflicts.map((c, i) => (
                                <li key={i}>
                                    <strong>Nr. {c.codes[0]} + Nr. {c.codes[1]}:</strong> {c.note}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
            {overLimit.length > 0 && (
                <div className="goa-line-editor__conflict-banner">
                    <TbAlertTriangle />
                    <div>
                        <strong>{t("invoicePage.goaMaxCountBadge", { count: overLimit.length })}</strong>
                        <ul>
                            {overLimit.map((v, i) => (
                                <li key={i}>
                                    <strong>Nr. {v.nr}:</strong> {v.note}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
            {missingBase.length > 0 && (
                <div className="goa-line-editor__conflict-banner">
                    <TbAlertTriangle />
                    <div>
                        <strong>{t("invoicePage.goaBaseCodeBadge", { count: missingBase.length })}</strong>
                        <ul>
                            {missingBase.map((v) => (
                                <li key={v.nr}>
                                    {t("invoicePage.goaBaseCodeLine", { nr: displayCode(v.nr), requires: requiredCodesLabel(v.requiresAnyOf) })}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </>
    );
}
